/**
 * AccountActions Component
 * 
 * Provides filtering, sorting, and export actions for accounts.
 * 
 * Features:
 * - Account type filtering
 * - Status filtering
 * - Net worth inclusion filtering
 * - Export functionality (CSV)
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React from 'react';
import { AccountType, AssetCategory } from '../../types/account.types';
import { Download, Filter, X } from 'lucide-react';

interface AccountActionsProps {
  onExportCSV: () => void;
  onFilterChange: (filter: {
    type?: AccountType;
    status?: 'active' | 'closed';
    includeInNetWorth?: boolean;
    category?: AssetCategory;
  }) => void;
  currentFilter: {
    type?: AccountType;
    status?: 'active' | 'closed';
    includeInNetWorth?: boolean;
    category?: AssetCategory;
  };
}

export function AccountActions({
  onExportCSV,
  onFilterChange,
  currentFilter
}: AccountActionsProps): React.ReactElement {
  const [showFilters, setShowFilters] = React.useState(false);

  const handleFilterChange = (key: string, value: string | boolean | undefined) => {
    onFilterChange({
      ...currentFilter,
      [key]: value === '' ? undefined : value,
    });
  };

  const clearFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters = Object.values(currentFilter).some(value => value !== undefined);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Filter Section */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 ${
                hasActiveFilters 
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800' 
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              aria-expanded={showFilters}
              aria-controls="account-filters"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-primary-600 text-white rounded-full font-semibold">
                  {Object.values(currentFilter).filter(v => v !== undefined).length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                aria-label="Clear all filters"
              >
                <X className="h-4 w-4" />
                <span>Clear Filters</span>
              </button>
            )}

            {/* Export Actions */}
            <button
              onClick={onExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200 font-medium text-sm"
              aria-label="Export accounts to CSV"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div id="account-filters" className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="main-page-grid-3">
              {/* Account Type Filter */}
              <div>
                <label htmlFor="type-filter" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Account Type
                </label>
                <select
                  id="type-filter"
                  value={currentFilter.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value as AccountType || undefined)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-2 px-3"
                >
                  <option value="">All Types</option>
                  <option value={AccountType.BANK_CASH}>Bank/Cash</option>
                  <option value={AccountType.SAVINGS}>Savings</option>
                  <option value={AccountType.CREDIT_CARD}>Credit Card</option>
                  <option value={AccountType.LOAN}>Loan</option>
                  <option value={AccountType.INVESTMENTS_SUPERANNUATION}>Investments/Super</option>
                  <option value={AccountType.ASSETS}>Assets</option>
                </select>
              </div>

              {/* Category Filter (only show when type is ASSETS) */}
              {currentFilter.type === AccountType.ASSETS && (
                <div>
                  <label htmlFor="category-filter" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Asset Category
                  </label>
                  <select
                    id="category-filter"
                    value={currentFilter.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value as AssetCategory || undefined)}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-2 px-3"
                  >
                    <option value="">All Categories</option>
                    {Object.values(AssetCategory).map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Status Filter */}
              <div>
                <label htmlFor="status-filter" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Status
                </label>
                <select
                  id="status-filter"
                  value={currentFilter.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value as 'active' | 'closed' || undefined)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-2 px-3"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Net Worth Filter */}
              <div>
                <label htmlFor="networth-filter" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Net Worth
                </label>
                <select
                  id="networth-filter"
                  value={currentFilter.includeInNetWorth === undefined ? '' : currentFilter.includeInNetWorth.toString()}
                  onChange={(e) => handleFilterChange('includeInNetWorth', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-2 px-3"
                >
                  <option value="">All Accounts</option>
                  <option value="true">Included in Net Worth</option>
                  <option value="false">Excluded from Net Worth</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
